import { booleanAttribute } from "#ce/utils/props";

const locationOptions = ["bottom", "bottom-left", "bottom-right"];

export const props = Object.freeze({
  /**
   * Suppress focus restoration on close when the caller owns the next focus target.
   *
   * @type {Boolean|String}
   * @default false
   */
  noRestoreFocus: booleanAttribute(),
  /**
   * Accessible name for the menu's bottom sheet, passed down because the visible title sits outside this
   * shadow root where an `aria-labelledby` IDREF cannot reach it.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Initial open state. Two-way via update-open event.
   *
   * @type {Boolean|String}
   * @default false
   */
  open: booleanAttribute(),
  /**
   * DOM id applied to the wrapper. Useful when the menu is anchored to a specific external element for tests.
   *
   * @type {String}
   * @default undefined
   */
  id: {
    type: String,
    default: undefined,
  },
  /**
   * Suppress menu activation entirely. The activator still renders but clicking it does nothing.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Close the menu when a click occurs inside the content. Disable for menus where users need to interact with the content (forms, multi-select) without dismissing.
   *
   * @type {Boolean|String}
   * @default true
   */
  closeOnContentClick: booleanAttribute(true),
  /**
   * Close the menu when a click occurs outside the activator and the content.
   *
   * @type {Boolean|String}
   * @default true
   */
  closeOnOutsideClick: booleanAttribute(true),
  /**
   * Show an arrow on the popup pointing at the activator. Has no effect on the mobile bottom-sheet variant.
   *
   * @type {Boolean|String}
   * @default false
   */
  pointer: booleanAttribute(),
  /**
   * On mobile, expand the bottom-sheet to its maximum height when the menu opens.
   *
   * @type {Boolean|String}
   * @default false
   */
  mobileExpandHeightMax: booleanAttribute(),
  /**
   * Anchor position of the popup relative to the activator. "bottom" centres the popup beneath the activator; "bottom-left" anchors to the activator's start edge; "bottom-right" anchors to the end edge.
   *
   * @type {String}
   * @default "bottom"
   * @storybook-control select
   */
  location: {
    type: String,
    default: "bottom",
    validator: (value) => locationOptions.includes(value),
  },
  /**
   * Keep the menu open at all times and remove the activator. Useful when sp-menu is embedded inside another surface that already handles open / close (e.g. the desktop column of a layout that shows a sidebar inline).
   *
   * @type {Boolean|String}
   * @default false
   */
  permanent: booleanAttribute(),
});
